import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { NewsLetterService } from 'src/app/services/news-letter/news-letter.service';
import { NewsLetterModalService } from 'src/app/services/news-letter-modal/news-letter-modal.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-news-letter',
  templateUrl: './add-edit-news-letter.component.html',
  styleUrls: ['./add-edit-news-letter.component.scss']
})
export class AddEditNewsLetterComponent extends DestructorComponent implements OnInit {

  @Output() newsLetterAdded = new EventEmitter<void>();
  @Output() newsLetterUpdated = new EventEmitter<void>();

  newsLetterForm: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedNewsLetter: any = null;

  // Model search properties
  modelSearchTerm = '';
  modelSearchResults: any[] = [];
  selectedModels: any[] = [];
  showModelDropdown = false;
  isModelSearching = false;
  private modelSearchSubject = new Subject<string>();

  // Make search properties
  makeSearchTerm = '';
  makeSearchResults: any[] = [];
  selectedMakes: any[] = [];
  showMakeDropdown = false;
  isMakeSearching = false;
  private makeSearchSubject = new Subject<string>();

  // Countries
  countriesList: any[] = [];
  selectedCountries: string[] = [];

  constructor(
    private fb: FormBuilder,
    private _newsLetterService: NewsLetterService,
    private _newsLetterModalService: NewsLetterModalService,
    private _globalStateService: GlobalStateService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
    this.setupModelSearchDebounce();
    this.setupMakeSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCountries();
  }

  private initializeForm(): void {
    this.newsLetterForm = this.fb.group({
      model_ids: [[]],
      models: [[]],
      make_ids: [[]],
      makes: [[]],
      countries: [[]],
      news_letter: ['save', [Validators.required]],
      audience: [[]],
      title: ['', [Validators.required]],
      description: ['']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_news_letter_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceNewsLetter = this._newsLetterModalService.getSelectedNewsLetter();
        if (serviceNewsLetter) {
          this.isEditMode = true;
          this.selectedNewsLetter = serviceNewsLetter;
          this.populateForm(this.selectedNewsLetter);
        } else {
          this.isEditMode = false;
          this.selectedNewsLetter = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._newsLetterModalService.clearSelectedNewsLetter();
        this.resetForm();
      });
    }
  }

  private loadCountries(): void {
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countriesList = countries || [];
      })
    );
  }

  private populateForm(newsLetter: any): void {
    this.newsLetterForm.patchValue({
      news_letter: newsLetter.news_letter || 'save',
      audience: newsLetter.audience || [],
      title: newsLetter.title || '',
      description: newsLetter.description || ''
    });

    // Populate selected models using both model_ids and models arrays
    if (newsLetter.model_ids && newsLetter.model_ids.length > 0) {
      const modelNames = newsLetter.models || [];
      this.selectedModels = newsLetter.model_ids.map((id: any, index: number) => {
        if (typeof id === 'object') {
          return id;
        }
        return { _id: id, name: modelNames[index] || id };
      });
      this.newsLetterForm.patchValue({
        model_ids: this.selectedModels.map((m: any) => m._id),
        models: this.selectedModels.map((m: any) => m.name)
      });
    }

    // Populate selected makes using both make_ids and makes arrays
    if (newsLetter.make_ids && newsLetter.make_ids.length > 0) {
      const makeNames = newsLetter.makes || [];
      this.selectedMakes = newsLetter.make_ids.map((id: any, index: number) => {
        if (typeof id === 'object') {
          return id;
        }
        return { _id: id, name: makeNames[index] || id };
      });
      this.newsLetterForm.patchValue({
        make_ids: this.selectedMakes.map((m: any) => m._id),
        makes: this.selectedMakes.map((m: any) => m.name)
      });
    }

    // Populate selected countries (expecting array of country codes)
    if (newsLetter.countries && newsLetter.countries.length > 0) {
      this.selectedCountries = newsLetter.countries.map((c: any) =>
        typeof c === 'object' ? c.code : c
      );
      this.newsLetterForm.patchValue({ countries: this.selectedCountries });
    }
  }

  private resetForm(): void {
    this.newsLetterForm.reset({
      model_ids: [],
      models: [],
      make_ids: [],
      makes: [],
      countries: [],
      news_letter: 'save',
      audience: [],
      title: '',
      description: ''
    });
    this.selectedModels = [];
    this.selectedMakes = [];
    this.selectedCountries = [];
    this.modelSearchTerm = '';
    this.makeSearchTerm = '';
    this.modelSearchResults = [];
    this.makeSearchResults = [];
    this.isLoading = false;
  }

  // Model search methods
  private setupModelSearchDebounce(): void {
    this.modelSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchModels(searchTerm);
    });
  }

  onModelSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.modelSearchTerm = target.value;
    if (this.modelSearchTerm.length >= 2) {
      this.modelSearchSubject.next(this.modelSearchTerm);
      this.showModelDropdown = true;
    } else {
      this.showModelDropdown = false;
      this.modelSearchResults = [];
    }
  }

  onModelInputFocus(): void {
    if (this.modelSearchResults.length > 0) {
      this.showModelDropdown = true;
    }
  }

  onModelInputBlur(): void {
    setTimeout(() => {
      this.showModelDropdown = false;
    }, 200);
  }

  private searchModels(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.modelSearchResults = [];
      this.showModelDropdown = false;
      return;
    }

    this.isModelSearching = true;
    this.subscriptions.push(
      this._generalService.GetModels().subscribe({
        next: (response: any) => {
          this.isModelSearching = false;
          if (response.status && response.data) {
            const allModels = response.data || [];
            this.modelSearchResults = allModels.filter((m: any) =>
              m.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
              !this.selectedModels.some(sm => sm._id === m._id)
            );
            this.showModelDropdown = this.modelSearchResults.length > 0;
          }
        },
        error: () => {
          this.isModelSearching = false;
          this.modelSearchResults = [];
        }
      })
    );
  }

  selectModel(model: any): void {
    if (!this.selectedModels.some(m => m._id === model._id)) {
      this.selectedModels.push(model);
      this.updateModelsFormValue();
    }
    this.modelSearchTerm = '';
    this.showModelDropdown = false;
    this.modelSearchResults = [];
  }

  removeModel(model: any): void {
    this.selectedModels = this.selectedModels.filter(m => m._id !== model._id);
    this.updateModelsFormValue();
  }

  private updateModelsFormValue(): void {
    this.newsLetterForm.patchValue({
      model_ids: this.selectedModels.map(m => m._id),
      models: this.selectedModels.map(m => m.name)
    });
  }

  // Make search methods
  private setupMakeSearchDebounce(): void {
    this.makeSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchMakes(searchTerm);
    });
  }

  onMakeSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.makeSearchTerm = target.value;
    if (this.makeSearchTerm.length >= 2) {
      this.makeSearchSubject.next(this.makeSearchTerm);
      this.showMakeDropdown = true;
    } else {
      this.showMakeDropdown = false;
      this.makeSearchResults = [];
    }
  }

  onMakeInputFocus(): void {
    if (this.makeSearchResults.length > 0) {
      this.showMakeDropdown = true;
    }
  }

  onMakeInputBlur(): void {
    setTimeout(() => {
      this.showMakeDropdown = false;
    }, 200);
  }

  private searchMakes(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.makeSearchResults = [];
      this.showMakeDropdown = false;
      return;
    }

    this.isMakeSearching = true;
    this.subscriptions.push(
      this._generalService.GetMakes().subscribe({
        next: (response: any) => {
          this.isMakeSearching = false;
          if (response.status && response.data) {
            const allMakes = response.data || [];
            this.makeSearchResults = allMakes.filter((m: any) =>
              m.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
              !this.selectedMakes.some(sm => sm._id === m._id)
            );
            this.showMakeDropdown = this.makeSearchResults.length > 0;
          }
        },
        error: () => {
          this.isMakeSearching = false;
          this.makeSearchResults = [];
        }
      })
    );
  }

  selectMake(make: any): void {
    if (!this.selectedMakes.some(m => m._id === make._id)) {
      this.selectedMakes.push(make);
      this.updateMakesFormValue();
    }
    this.makeSearchTerm = '';
    this.showMakeDropdown = false;
    this.makeSearchResults = [];
  }

  removeMake(make: any): void {
    this.selectedMakes = this.selectedMakes.filter(m => m._id !== make._id);
    this.updateMakesFormValue();
  }

  private updateMakesFormValue(): void {
    this.newsLetterForm.patchValue({
      make_ids: this.selectedMakes.map(m => m._id),
      makes: this.selectedMakes.map(m => m.name)
    });
  }

  // Country multi-select methods
  onCountryChange(event: Event, countryCode: string): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      if (!this.selectedCountries.includes(countryCode)) {
        this.selectedCountries.push(countryCode);
      }
    } else {
      this.selectedCountries = this.selectedCountries.filter(c => c !== countryCode);
    }
    this.newsLetterForm.patchValue({ countries: this.selectedCountries });
  }

  isCountrySelected(countryCode: string): boolean {
    return this.selectedCountries.includes(countryCode);
  }

  // Audience checkbox methods
  onAudienceChange(event: Event, value: string): void {
    const checkbox = event.target as HTMLInputElement;
    let audience = this.newsLetterForm.get('audience')?.value || [];
    if (checkbox.checked) {
      if (!audience.includes(value)) {
        audience = [...audience, value];
      }
    } else {
      audience = audience.filter((a: string) => a !== value);
    }
    this.newsLetterForm.patchValue({ audience });
  }

  isAudienceSelected(value: string): boolean {
    const audience = this.newsLetterForm.get('audience')?.value || [];
    return audience.includes(value);
  }

  onSubmit(): void {
    if (this.newsLetterForm.valid && !this.isLoading) {
      this.isLoading = true;
      const formData = { ...this.newsLetterForm.value };

      if (this.isEditMode && this.selectedNewsLetter) {
        this.updateNewsLetter(formData);
      } else {
        this.addNewsLetter(formData);
      }
    } else {
      this.markFormGroupTouched(this.newsLetterForm);
    }
  }

  private addNewsLetter(data: any): void {
    this.subscriptions.push(
      this._newsLetterService.AddData(data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'News letter added successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.newsLetterAdded.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to add news letter');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while adding news letter');
        }
      })
    );
  }

  private updateNewsLetter(data: any): void {
    const id = this.selectedNewsLetter._id || this.selectedNewsLetter.id;
    this.subscriptions.push(
      this._newsLetterService.UpdateData(id, data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'News letter updated successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.newsLetterUpdated.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to update news letter');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while updating news letter');
        }
      })
    );
  }

  private handleError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_news_letter_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Form control getters
  get title() { return this.newsLetterForm.get('title'); }
  get news_letter() { return this.newsLetterForm.get('news_letter'); }
  get description() { return this.newsLetterForm.get('description'); }
}
